import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import {
  useEffectiveRole,
  useImpersonatedUnitId,
} from "../lib/view-as-context";
import { RequestMaintenanceDialog } from "./RequestMaintenanceDialog";
import {
  ScheduleMaintenanceDialog,
  type SchedulableTask,
} from "./ScheduleMaintenanceDialog";
import {
  VisitDetailDialog,
  type VisitDetail,
} from "./VisitDetailDialog";

type Role = "landlord" | "tenant";
type Status = "requested" | "scheduled" | "completed" | "cancelled";
type Priority = "low" | "normal" | "high" | "urgent";

interface ScheduleTask {
  _id: string;
  title: string;
  description: string | null;
  status: Status;
  scheduledFor: string | null;
  completedAt: number | null;
  recurringIntervalDays: number | null;
  priority: Priority;
  requestedByUserId: string | null;
  requestedBy: {
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
  contractor: { _id: string; name: string; serviceType: string } | null;
  unit: { _id: string; label: string } | null;
  hasUnread: boolean;
}

interface ScheduleResult {
  role: Role;
  tasks: ScheduleTask[];
  myUserId: string;
}

const priorityRank: Record<Priority, number> = {
  urgent: 3,
  high: 2,
  normal: 1,
  low: 0,
};

// Open maintenance/upgrade requests — tenant-submitted issues without
// a scheduled date. Sorted by priority desc; the requester (or
// landlord) can change a row's priority inline.
export function RequestsSection() {
  const property = useProperty();
  const result = useQuery(
    anyApi.maintenance.listVisible,
    property ? { propertySlug: property.slug } : "skip",
  ) as ScheduleResult | null | undefined;
  const role = useEffectiveRole(result?.role);
  const isLandlord = role === "landlord";
  const myUserId = result?.myUserId ?? null;

  const [requestOpen, setRequestOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<SchedulableTask | null>(null);
  const [viewTask, setViewTask] = useState<VisitDetail | null>(null);
  const [viewOpen, setViewOpen] = useState(false);

  // Landlord-as-tenant impersonation: hide tasks scoped to other
  // units; property-wide passes through.
  const impersonatedUnitId = useImpersonatedUnitId();
  const visibleTasks = useMemo(() => {
    const all = result?.tasks ?? [];
    if (impersonatedUnitId === null) return all;
    return all.filter((t) => !t.unit || t.unit._id === impersonatedUnitId);
  }, [result?.tasks, impersonatedUnitId]);

  const requests = visibleTasks
    .filter((t) => t.status === "requested")
    .slice()
    .sort((a, b) => {
      const dp = priorityRank[b.priority] - priorityRank[a.priority];
      if (dp !== 0) return dp;
      return a._id.localeCompare(b._id);
    });

  const openView = (task: ScheduleTask) => {
    setViewTask(toVisitDetail(task));
    setViewOpen(true);
  };

  const handleScheduleFromView = () => {
    if (!viewTask) return;
    const task = visibleTasks.find((t) => t._id === viewTask._id);
    setViewOpen(false);
    if (task) {
      setEditingTask(toSchedulable(task));
      setScheduleOpen(true);
    }
  };

  return (
    <>
      <section className="rounded-xl border border-slate-200 bg-white">
        <header className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Maintenance/Upgrade requests
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {isLandlord
                ? "Sorted by priority. Click any request to schedule it."
                : "Submit a request and the landlord will schedule it. Set a higher priority on the ones that matter most."}
            </p>
          </div>
          {(role === "tenant" || role === "landlord") && (
            <button
              type="button"
              onClick={() => setRequestOpen(true)}
              className="shrink-0 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors"
            >
              {isLandlord ? "Add request" : "Request maintenance/upgrade"}
            </button>
          )}
        </header>
        <Body
          requests={requests}
          loading={result === undefined}
          isLandlord={isLandlord}
          myUserId={myUserId}
          onOpen={openView}
        />
      </section>

      <RequestMaintenanceDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        mode={isLandlord ? "landlord" : "tenant"}
        propertySlug={property?.slug}
      />
      {property && (
        <ScheduleMaintenanceDialog
          open={scheduleOpen}
          onClose={() => {
            setScheduleOpen(false);
            setEditingTask(null);
          }}
          propertySlug={property.slug}
          existingTask={editingTask ?? undefined}
        />
      )}
      <VisitDetailDialog
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewTask(null);
        }}
        task={viewTask}
        isLandlord={isLandlord}
        onEdit={handleScheduleFromView}
      />
    </>
  );
}

function Body({
  requests,
  loading,
  isLandlord,
  myUserId,
  onOpen,
}: {
  requests: ScheduleTask[];
  loading: boolean;
  isLandlord: boolean;
  myUserId: string | null;
  onOpen: (task: ScheduleTask) => void;
}) {
  if (loading) {
    return (
      <div className="px-6 py-4 space-y-4">
        {[0, 1].map((i) => (
          <div key={i} className="animate-pulse space-y-2">
            <div className="h-4 w-2/3 rounded bg-slate-100" />
            <div className="h-3 w-1/2 rounded bg-slate-100" />
          </div>
        ))}
      </div>
    );
  }
  if (requests.length === 0) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-sm font-medium text-slate-900">
          No open requests.
        </p>
        <p className="mt-1 text-sm text-slate-500">
          {isLandlord
            ? "Nothing waiting on you right now."
            : "Submit one whenever something needs attention or upgrading."}
        </p>
      </div>
    );
  }
  return (
    <ul className="px-6 py-4 divide-y divide-slate-100">
      {requests.map((task) => (
        <Row
          key={task._id}
          task={task}
          isLandlord={isLandlord}
          myUserId={myUserId}
          onOpen={onOpen}
        />
      ))}
    </ul>
  );
}

function Row({
  task,
  isLandlord,
  myUserId,
  onOpen,
}: {
  task: ScheduleTask;
  isLandlord: boolean;
  myUserId: string | null;
  onOpen: (task: ScheduleTask) => void;
}) {
  const setPriority = useMutation(anyApi.maintenance.setPriority);

  const isMine = myUserId !== null && task.requestedByUserId === myUserId;
  const canEditPriority = isLandlord || isMine;

  const handlePriorityChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    e.stopPropagation();
    await setPriority({
      taskId: task._id,
      priority: e.target.value as Priority,
    });
  };

  return (
    <li
      onClick={() => onOpen(task)}
      className="cursor-pointer py-4 first:pt-0 flex items-start justify-between gap-4 hover:bg-slate-50/50 -mx-2 px-2 rounded-md transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-slate-900">{task.title}</p>
          {canEditPriority ? (
            <PrioritySelect
              value={task.priority}
              onChange={handlePriorityChange}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <PriorityBadge priority={task.priority} />
          )}
          {task.hasUnread && (
            <span
              title="New activity"
              className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-blue-700"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              New
            </span>
          )}
        </div>
        {task.description && (
          <p className="mt-1 text-sm text-slate-500 leading-relaxed">
            {task.description}
          </p>
        )}
        <p className="mt-1.5 text-xs text-slate-500">
          {[
            task.requestedBy ? `From ${requesterName(task.requestedBy)}` : null,
            task.unit ? task.unit.label : "Property-wide",
            task.contractor
              ? `Suggested: ${task.contractor.name}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      {isLandlord && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(task);
          }}
          className="shrink-0 self-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors"
        >
          Open
        </button>
      )}
    </li>
  );
}

function toVisitDetail(task: ScheduleTask): VisitDetail {
  return {
    _id: task._id,
    title: task.title,
    description: task.description,
    status: task.status,
    scheduledFor: task.scheduledFor,
    completedAt: task.completedAt,
    recurringIntervalDays: task.recurringIntervalDays,
    contractor: task.contractor
      ? {
          name: task.contractor.name,
          serviceType: task.contractor.serviceType,
        }
      : null,
    unit: task.unit ? { label: task.unit.label } : null,
  };
}

const priorityStyles: Record<Priority, string> = {
  urgent: "bg-red-50 text-red-700 ring-red-200",
  high: "bg-orange-50 text-orange-700 ring-orange-200",
  normal: "bg-slate-100 text-slate-600 ring-slate-200",
  low: "bg-slate-50 text-slate-500 ring-slate-200",
};

function PriorityBadge({ priority }: { priority: Priority }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${priorityStyles[priority]}`}
    >
      {priority}
    </span>
  );
}

function PrioritySelect({
  value,
  onChange,
  onClick,
}: {
  value: Priority;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <select
      value={value}
      onChange={onChange}
      onClick={onClick}
      title="Priority"
      className={`rounded-full border-0 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset focus:outline-none focus:ring-2 focus:ring-slate-900/20 ${priorityStyles[value]}`}
    >
      <option value="urgent">Urgent</option>
      <option value="high">High</option>
      <option value="normal">Normal</option>
      <option value="low">Low</option>
    </select>
  );
}

function requesterName(r: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}): string {
  if (r.firstName) {
    return r.lastName ? `${r.firstName} ${r.lastName.charAt(0)}.` : r.firstName;
  }
  return r.email.split("@")[0];
}

function toSchedulable(task: ScheduleTask): SchedulableTask {
  return {
    _id: task._id,
    title: task.title,
    description: task.description,
    scheduledFor: task.scheduledFor,
    recurringIntervalDays: task.recurringIntervalDays,
    contractor: task.contractor ? { _id: task.contractor._id } : null,
    unit: task.unit ? { _id: task.unit._id } : null,
  };
}
